import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Stack, Typography } from "@mui/material";
import { styled } from "@financeable-com-au/financeable-ui";
import { isEqual, merge, uniqWith, isArray } from "lodash";
import moment from "moment";
import { LenderProductCard } from "@/components/LenderProductCard";
import { useCalculateApplication } from "@/hooks/useCalculateApplication";
import { getFilteredLenders } from "@/utils/commonFunctions";
import ProductSelectorFilterBy from "./ProductSelectorFilterBy";

import ProductSelectorOrderBy from "./ProductSelectorOrderBy";
import { rankProducts } from "./rankProducts";
import calculateEffectiveRate from "../../utils/calculateEffectiveRate";
import calculateRepayments from "../../utils/calculateRepayments";
import { evaluateCriteria, calculateLoading, calculateFees, calculateExtras } from "../../utils/evaluateCriteria";
import getMonthlyRate from "../../utils/getMonthlyRate";
import {
  productTypes,
  productLenderFilterKey,
  getCriteriaFilterList,
  getIsConsumerOrPersonalApplication,
} from "../../constants";
import { userPermissionsSelector, userSelector } from "../../store/slices/userSlice";
import { getLocalStorage, setLocalStorage } from "../../utils/localstorage";
import {
  applicationSelector,
  assetSelector,
  loanDetailsSelector,
  userDetailsSelector,
  entitySelector,
  customersSelector,
} from "../../store/slices/applicationFormSlice";
import { FeeFrequency } from "../../store/slices/types/applicationFormSlice";
import { getTotalFeesByFrequency } from "../../utils/lenderUtils";
import { SendProductQuotesBar } from "../ProductsQuotes/SendProductQuotesBar";

const StickySendQuotesBar = styled("div")({
  position: "sticky",
  bottom: -10,
  zIndex: 2,
  paddingTop: 8,
  background: "#ffffff",
});

const ProductSelector = React.memo(({ onShowRequired, lenderData }) => {
  const [fitFilterValue, setFitFilterValue] = useState("3 criteria off");

  const { open, isQuoteOpen } = useSelector(userDetailsSelector);
  const isApplication = !isQuoteOpen;
  const application = useSelector(applicationSelector);
  const isCommercial = application.applicationType === "commercial";
  const isConsumerOrPersonalApplication = getIsConsumerOrPersonalApplication(application.applicationType);
  const { getBrokerageAmount, getFinanceAmount, getFeeAmount, getLoanAmount } = useCalculateApplication();

  const asset = useSelector(assetSelector);
  const loanDetails = useSelector(loanDetailsSelector);
  const entity = useSelector(entitySelector);
  const customers = useSelector(customersSelector);
  const user = useSelector(userSelector);
  const permissions = useSelector(userPermissionsSelector);
  const introducerDeclarationAgree = application?.introducerDeclarationAgree;
  const criteriaFilterListDefault = getCriteriaFilterList[application.applicationType];

  const [criteriaFilterValue, setCriteriaFilterValue] = useState([]);
  const [criteriaFilterList, setCriteriaFilterList] = useState(criteriaFilterListDefault);

  // Lenders products list.
  const rawProducts = structuredClone(lenderData?.data?.getAllLendersData?.docs ?? []);
  const rawLenderList = structuredClone(lenderData?.data?.getAllLendersData?.docs ?? []).map((lender) => {
    const { lender: lenderName } = lender;
    return lenderName;
  });
  const products =
    permissions && permissions.lenders
      ? getFilteredLenders({
          applicationType: application.applicationType,
          permittedLenders: permissions,
          lenderList: rawProducts,
          isProducts: true,
        })
      : [];

  const lenderList =
    permissions && permissions.lenders
      ? getFilteredLenders({
          applicationType: application.applicationType,
          permittedLenders: permissions,
          lenderList: rawLenderList,
          isSorted: true,
        })
      : [];

  const isSingleLenderProduct = products.length === 1;

  const {
    assetValue = 0,
    ageOfAsset = "",
    assetType = "",
    typeOfSale,
    glassesMake,
    glassesModel,
    glassesVariant,
    make,
    model,
  } = useSelector(assetSelector);

  const {
    balloon = 0,
    term = 0,
    tradeIn = 0,
    payout = 0,
    deposit = 0,
    brokerage = 0,
    rateAdjustment = 0,
    purpose,
    originationFee = 0,
    loanAmount: loanAmountSaved,
  } = useSelector(loanDetailsSelector);

  const [productsMap, setProductsMap] = useState([]);
  // const [orderByParameters, setOrderByParameters] = useState([]);
  const [orderByParameters, setOrderByParameters] = useState([
    ["fit", "asc"],
    ["repayments", "asc"],
  ]);
  // const [orderByParametersList, setOrderByParametersList] = useState([]);
  const [orderByParametersList, setOrderByParametersList] = useState([
    ["fit", "repayments"],
    ["asc", "asc"],
  ]);

  const [filterByParameters, setFilterByParameters] = useState(lenderList);

  const [filterByProductType, setFilterByProductType] = useState(productTypes);

  const [screenSize, getDimension] = useState({
    dynamicWidth: window.innerWidth,
    dynamicHeight: window.innerHeight,
  });

  useEffect(() => {
    window.addEventListener("resize", handle.setDimension);
    return () => {
      window.removeEventListener("resize", handle.setDimension);
    };
  }, [screenSize]);

  const handle = {
    setDimension: () => {
      getDimension({
        dynamicWidth: window.innerWidth,
        dynamicHeight: window.innerHeight,
      });
    },
    loanAmountCommercial: (fees, netAssetValue, brokerageAmount) => netAssetValue + brokerageAmount + fees,
    loanAmountConsumer: (fees, netAssetValue) => netAssetValue + fees,
    evaluateProducts: () => {
      let firstCustomer = customers?.[0];
      if (firstCustomer?.dateOfBirth) {
        firstCustomer = {
          ...firstCustomer,
          dateOfBirth: moment().diff(firstCustomer?.dateOfBirth, "years"),
        };
      }
      const customerProfile = firstCustomer?.customerProfile;
      let creditProfile = firstCustomer?.creditProfile;
      let employer = firstCustomer?.employers?.[0];

      if (firstCustomer?.employers?.length >= 1) {
        const totalEmploymentTime = firstCustomer?.employers?.reduce(
          (total, employer) => total + +employer?.timeOfEmployment,
          0,
        );

        employer = {
          ...employer,
          timeOfEmployment: totalEmploymentTime || 0,
        };
      }

      if (firstCustomer?.creditProfile?.isManualScore) {
        creditProfile = {
          ...creditProfile,
          score: creditProfile.manualScore || 0,
        };
      }

      let updatedAsset = asset;

      if (asset?.ageOfAsset) {
        updatedAsset = {
          ...asset,
          ageOfAsset: moment().diff(asset?.ageOfAsset, "months"),
          ...(asset?.ageOfAssetAtEnd && { ageOfAssetAtEnd: +asset?.ageOfAssetAtEnd }),
        };
      }

      let updatedEntity = entity?.[0];

      if (updatedEntity?.gst != null || updatedEntity?.timeInBusiness != null) {
        updatedEntity = {
          ...updatedEntity,
          gst: moment().diff(updatedEntity?.gst, "months"),
          timeInBusiness: moment().diff(updatedEntity?.timeInBusiness, "months"),
        };
      }

      for (const en of entity) {
        if (en?.continuity) {
          updatedEntity = {
            ...updatedEntity,
            gst: (updatedEntity?.gst || 0) + moment().diff(en?.gst, "months"),
            timeInBusiness: (updatedEntity?.timeInBusiness || 0) + moment().diff(en?.timeInBusiness, "months"),
          };
        }
      }

      let criteriaEntity = updatedEntity

      if (updatedEntity?.gst <= 0) {
        let gstValue = entity?.[0]?.gst ? 1 : 0;

        if (gstValue === 0) {
          for (const en of entity) {
            if (en?.continuity) {
              gstValue += en?.gst ? 1 : 0;
            }
          }
        }

        criteriaEntity = {
          ...updatedEntity,
          gst: gstValue,
        };
      }

      const productList = products
        ?.flatMap((product) =>
          product?.productTiers?.map((tier) => {
            if (!tier) return null;

            const newLoanDetails = {
              loanAmount: assetValue - deposit,
              term,
              deposit,
              balloon,
            };

            const FEESLIST = calculateFees(product.fees, {
              asset: updatedAsset,
              loanDetails: newLoanDetails,
              entity: updatedEntity,
              customer: firstCustomer,
              customerProfile,
              creditProfile,
              employer,
            });

            const feesList = FEESLIST.map((fee) => {
              const feeValue = fee.value * 1;
              if (fee.calc === "percent") {
                const newFeeValue = (feeValue / 100) * newLoanDetails.loanAmount;
                return { ...fee, value: newFeeValue };
              }
              return fee;
            });

            const fees = FEESLIST.map((fee) => {
              if (fee.calc === "percent") {
                const feeAmount = getFeeAmount({ feePercentage: fee.value ?? 0 });
                return { ...fee, value: feeAmount };
              }
              return fee;
            }).map((fee) => {
              if (fee.name.toUpperCase() === "ORIGINATION FEE" && originationFee < (fee.value ?? 0)) {
                return { ...fee, value: originationFee };
              }
              return fee;
            });

            const totalFinancedFees = getTotalFeesByFrequency({ fees, frequency: FeeFrequency.Financed });
            const totalMonthlyFees = getTotalFeesByFrequency({ fees, frequency: FeeFrequency.Monthly });

            const feesTotal = feesList.reduce((accum, fee) => {
              let feeValue = fee.value * 1;
              if (fee.name.toUpperCase() === "ORIGINATION FEE") {
                feeValue = originationFee < fee.value ? parseFloat(originationFee) : fee.value;
              }
              if (fee.frequency === "monthly") return accum;
              if (fee.capitalised) return feeValue + accum;
              return accum;
            }, 0);

            const netAssetValue = assetValue * 1 - deposit * 1 - tradeIn * 1 + payout * 1;
            const adjustedBrokerage = brokerage * 1;

            // Loan amount does not include brokerage.
            const loanAmount = getLoanAmount({ totalFinancedFees });
            const defaultBrokerage = calculateExtras(product.brokerage, {
              asset: updatedAsset,
              loanDetails: {
                ...loanDetails,
                loanAmount,
              },
              entity: updatedEntity,
            })[0];

            let finalBrokerage = adjustedBrokerage > defaultBrokerage?.max ? defaultBrokerage?.max : adjustedBrokerage;
            if (isConsumerOrPersonalApplication) {
              if (rateAdjustment === -2) finalBrokerage = 0;
              if (rateAdjustment === -1.5) finalBrokerage = defaultBrokerage?.value * 0.25;
              if (rateAdjustment === -1) finalBrokerage = defaultBrokerage?.value * 0.5;
              if (rateAdjustment === -0.5) finalBrokerage = defaultBrokerage?.value * 0.75;
              if (rateAdjustment === 0) finalBrokerage = defaultBrokerage?.value;
            }
            const brokerageAmount = getBrokerageAmount({ brokeragePercentage: finalBrokerage });

            // We just need financed fees only.
            // Finance amount includes brokerage (when applicable).
            const financeAmount = getFinanceAmount({
              totalFinancedFees,
              brokerageAmount,
            });

            const loadingList = calculateLoading(product.loading, {
              asset: updatedAsset,
              loanDetails: {
                ...loanDetails,
                ...{
                  loanAmount: financeAmount,
                  brokerageRate: adjustedBrokerage,
                  deposit: 100 - ((assetValue - deposit) / assetValue) * 100,
                },
              },
              entity: updatedEntity,
              customerProfile,
            });

            const loadingTotal = loadingList.reduce(
              (accum, item) => (item.result === true ? item.value + accum : accum),
              0,
            );

            let RATE;
            if (Array.isArray(tier.rate)) {
              const scoreCriteria = tier.criteria.find((criteria) => criteria.attribute === "score");
              if (
                scoreCriteria &&
                creditProfile?.score >= scoreCriteria.value.min &&
                creditProfile?.score < scoreCriteria.value.max
              ) {
                const percentage =
                  ((creditProfile.score - scoreCriteria.value.max) /
                    (scoreCriteria.value.max - scoreCriteria.value.min)) *
                  -100;
                RATE = (percentage / 100) * (Number(tier.rate[1]) - Number(tier.rate[0])) + Number(tier.rate[0]);
              } else {
                RATE = Number(tier.rate[0]);
              }
            } else {
              RATE = Number(tier.rate);
            }

            RATE = RATE + rateAdjustment + loadingTotal;
            const quoteDetails = handle.calculateQuote(financeAmount, balloon, term, RATE, netAssetValue);
            const monthlyRepayments = quoteDetails?.repayments + totalMonthlyFees;
            const balloonAmount = (balloon / 100) * netAssetValue;
            const presentValue = balloonAmount / Math.pow(1 + RATE, term);
            const trueLoanAmount = financeAmount - presentValue;

            const comparisonRateRaw = calculateEffectiveRate(
              term,
              quoteDetails.repayments,
              -(trueLoanAmount - feesTotal - (isCommercial ? brokerageAmount : 0)),
              balloonAmount,
              0,
              RATE / 100,
            );

            let effectiveRate = calculateEffectiveRate(
              term,
              quoteDetails.repayments,
              -(netAssetValue + totalFinancedFees),
              balloonAmount,
              0,
              RATE / 100,
            );

            if (effectiveRate < RATE) {
              effectiveRate = RATE;
            }

            let comparisonRate = comparisonRateRaw;

            if (comparisonRate < RATE) {
              comparisonRate = RATE;
            }

            const LOAN_DETAILS = {
              ...loanDetails,
              ...{
                loanAmount: financeAmount,
                repayments: quoteDetails.repayments,
                deposit: 100 - ((assetValue - deposit) / assetValue) * 100,
              },
            };

            const productDetails = {
              lenderId: product?._id,
              lender: product.lender,
              productName: tier.name,
              name: tier.name,
              logo: product.logoURL,
              lenderCredentials: product?.lenderCredentials,
              requiredFieldsData: undefined,
              rawRequiredFields: product.requiredFields,
              conditionalFields: product.conditionalFields,
              submissionSteps: product.submissionSteps,
            };

            const criteriaResults = evaluateCriteria(tier.criteria, {
              asset: updatedAsset,
              loanDetails: LOAN_DETAILS,
              entity: criteriaEntity, // entity?.[0]
              customer: firstCustomer,
              customerProfile,
              creditProfile,
              employer,
            }).filter((result) => {
              if (!result.criteria) return result;
              if (result.criteria.every((criteria) => criteria.result)) return result;
              if (
                !result.criteria.some((criteria) => criteria.result) &&
                result.criteria.some((item) => result.primary === item.attribute)
              ) {
                return result.criteria.find((item) => result.primary === item.attribute);
              }
              return result;
            });

            const overallResult = criteriaResults.reduce((count, item) => (item?.result ? count + 1 : count), 0);

            function tierInfo() {
              return tier?.info?.[0] ? tier?.info : [];
            }

            const rateRange =
              Array.isArray(tier.rate)
                ? {
                    min: Number(tier.rate[0]),
                    max: Number(tier.rate[1]),
                  }
                : undefined;

            return merge(productDetails, {
              effectiveRate: undefined,
              comparisonRate: undefined,
              financeAmount: undefined,
              loanAmount, // calculate for order by filter
              repayments: monthlyRepayments, // calculate for order by filter
              brokerage: finalBrokerage,
              brokerageAmount: brokerageAmount,
              brokerageMax: defaultBrokerage?.max ?? 0,
              monthlyFees: undefined,
              fees: FEESLIST,
              allFees: product.fees ?? [],
              feesTotal: feesTotal, // calculate for order by filter
              financeFeesTotal: undefined,
              loading: { loadingList, loadingTotal },
              criteriaResults,
              fit: criteriaResults.length - overallResult,
              criteria: `${overallResult}/${criteriaResults.length}`,
              qualification: criteriaResults.length - overallResult,
              productType: tier.productType,
              labels: [tier.productType],
              rate: Number(RATE),
              rateRange,
              info: [...tierInfo(), ...(product?.info || [])],
              date: product.date,
              isCommercial: undefined,
              actions: product.actions,
              lodgeAI: product.lodgeAI,
              isApiLodge: product.isApiLodge,
              disclaimer: product.disclaimer,
            });
          }),
        )
        .filter(Boolean);

      const list = productList
        .flat()
        .filter((product) => filterByParameters?.includes(product?.lender))
        .filter((product) => filterByProductType?.includes(product?.productType))
        .filter((product) => handle.filterByCriteriaFn(product, criteriaFilterValue));

      setProductsMap(
        rankProducts(list, criteriaFilterValue, orderByParametersList, {
          professionalServices: criteriaEntity?.professionalServices,
        }),
      );
    },
    filterByCriteriaFn: (product, criteriaFilterValue) => {
      const criteriaResults = product.criteriaResults.map((result) => {
        if (result.result || result.result === undefined) return result.attribute;
        return result.result;
      });
      const matches = Array.isArray(criteriaFilterValue)
        ? criteriaFilterValue.map((criteria) => criteriaResults.includes(criteria))
        : [criteriaResults.includes(criteriaFilterValue)];

      return matches.every((i) => i);
    },
    calculateQuote: (loanAmount, balloon, term, rate, netAssetValue) => {
      const monthlyRate = getMonthlyRate(rate);
      const repayments = calculateRepayments(
        monthlyRate,
        term,
        -loanAmount,
        (balloon / 100) * netAssetValue,
        0,
      );

      return {
        repayments: Number(repayments.toFixed(2)),
      };
    },
    orderByFn: (event) => {
      const targetValue = event;
      handle.setFilterToLocalStore("orderBy", targetValue);
    },
    filterByFn: (event) => {
      handle.setFilterToLocalStore("filterBy", event.target.value);
    },
    filterByProductTypeFn: (event) => {
      handle.setFilterToLocalStore("filterByProductType", event.target.value);
    },
    filterByFitFn(event) {
      handle.setFilterToLocalStore("filterByFit", event.target.value);
    },
    filterByCriteria: (event) => {
      handle.setFilterToLocalStore("filterByCriteria", event.target.value);
    },
    setFilterToLocalStore: (key, filteredValue) => {
      const getProductFilter = getLocalStorage(productLenderFilterKey);

      if (!getProductFilter) {
        setCriteriaFilterList(criteriaFilterListDefault);
        setLocalStorage(productLenderFilterKey, null);
      }

      let filteredData = {
        orderBy: orderByParameters,
        orderByList: orderByParametersList,
        filterBy: filterByParameters,
        filterByProductType: filterByProductType,
        filterByFit: fitFilterValue,
        filterByCriteria: criteriaFilterValue,
      };

      switch (key) {
        case "orderBy":
          const uniqOrderBy = uniqWith(filteredValue, isEqual);
          setOrderByParameters(uniqOrderBy);
          filteredData.orderBy = uniqOrderBy;

          const targetValue = filteredValue;
          const params = targetValue.map((i) => i[0]);
          const order = targetValue.map((i) => i[1]);
          setOrderByParametersList([params, order]);
          filteredData.orderByList = [params, order];
          break;
        case "orderByList":
          setOrderByParametersList(filteredValue);
          filteredData.orderByList = filteredValue;
          break;
        case "filterBy":
          setFilterByParameters(filteredValue);
          filteredData.filterBy = filteredValue;
          break;
        case "filterByProductType":
          setFilterByProductType(filteredValue);
          filteredData.filterByProductType = filteredValue;
          break;
        case "filterByFit":
          setFitFilterValue(filteredValue);
          filteredData.filterByFit = filteredValue;
          break;
        case "filterByCriteria":
          setCriteriaFilterValue(filteredValue);
          filteredData.filterByCriteria = filteredValue;
          break;

        default:
          break;
      }

      const data = {
        ...getProductFilter,
        [application._id]: filteredData,
      };

      setLocalStorage(productLenderFilterKey, data);
    },
  };

  useEffect(() => {
    handle.evaluateProducts();
  }, [
    customers,
    introducerDeclarationAgree,
    brokerage,
    assetType,
    ageOfAsset,
    balloon,
    term,
    entity,
    orderByParameters,
    filterByParameters,
    filterByProductType,
    fitFilterValue,
    typeOfSale,
    criteriaFilterValue,
    assetValue,
    deposit,
    tradeIn,
    payout,
    glassesMake,
    glassesModel,
    glassesVariant,
    make,
    model,
    asset,
    loanAmountSaved,
    originationFee,
    purpose,
    rateAdjustment,
  ]);

  useEffect(() => {
    // Update Filter list
    const getProductFilter = getLocalStorage(productLenderFilterKey);
    if (getProductFilter) {
      const isFilterData = getProductFilter[application._id];
      if (isFilterData) {
        const { orderBy, orderByList, filterBy, filterByProductType, filterByFit, filterByCriteria } =
          getProductFilter[application._id];

        if (orderByList?.[0]?.length <= 0) {
          setOrderByParameters([
            ["fit", "asc"],
            ["repayments", "asc"],
          ]);

          setOrderByParametersList([
            ["fit", "repayments"],
            ["asc", "asc"],
          ]);
        } else {
          setOrderByParameters(orderBy);
          setOrderByParametersList(orderByList);
        }
        setFilterByParameters(filterBy);
        setFilterByProductType(filterByProductType);
        setFitFilterValue(filterByFit);
        setCriteriaFilterValue(filterByCriteria);
        return;
      }
    }
  }, [application]);

  const productsList = useMemo(
    () =>
      productsMap?.slice(0, 50).map((productDetails, i) => {
        const id = `${productDetails.lender.replaceAll(" ", "-")}-${i}`;
        return (
          <LenderProductCard
            key={id}
            productDetails={productDetails}
            showRequiredFields={onShowRequired}
            isSingleLenderProduct={isSingleLenderProduct}
          />
        );
      }),
    [productsMap],
  );

  return (
    <React.Fragment>
      <Stack
        direction="row"
        style={{
          width: "100%",
          justifyContent: "space-between",
          alignItems: "baseline",
        }}
      >
        {open && (
          <Stack direction="row">
            <ProductSelectorFilterBy
              list={lenderList}
              handleFilterBy={handle.filterByFn}
              value={filterByParameters}
              title="Lenders"
              multiple={isArray(filterByParameters)}
              screenSize={screenSize}
            />
            <ProductSelectorFilterBy
              list={productTypes}
              handleFilterBy={handle.filterByProductTypeFn}
              value={filterByProductType}
              title="Product type"
              multiple={isArray(filterByProductType)}
              screenSize={screenSize}
            />
            <ProductSelectorFilterBy
              list={criteriaFilterList}
              handleFilterBy={handle.filterByCriteria}
              value={criteriaFilterValue}
              title="Must match"
              multiple={isArray(criteriaFilterValue)}
              screenSize={screenSize}
            />
          </Stack>
        )}
        {!isSingleLenderProduct && (
          <ProductSelectorOrderBy handleOrderBy={handle.orderByFn} value={orderByParameters} screenSize={screenSize} />
        )}
      </Stack>

      <Stack direction="column" rowGap={1.5}>
        {productsList}
      </Stack>

      {!productsMap?.[0] && (
        <Stack justifyContent="center" alignItems="center" style={{ height: "200px" }}>
          <Typography>No matching products</Typography>
        </Stack>
      )}

      {open && isApplication && application?.productsQuotes?.length > 0 && (
        <StickySendQuotesBar>
          <SendProductQuotesBar productsQuotes={application.productsQuotes} customers={customers ?? []} />
        </StickySendQuotesBar>
      )}
    </React.Fragment>
  );
});

export default ProductSelector;
